import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    withStyles,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Box,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from "@material-ui/core";
import { Controller } from "../controllers";

const styles = theme => ({
    root: {
        padding: 20,
        marginTop: 20
    },
    table: {
        minWidth: 650
    },
    tabContainer: {
        marginTop: theme.spacing(2),
        backgroundColor: theme.palette.background.paper
    },
    downloadButton: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    dateInput: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        marginRight: theme.spacing(2)
    },
    groupSelect: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        marginRight: theme.spacing(2),
        minWidth: 150
    }
});

const Rapid7Inventory = ({ classes }) => {
    const state = useSelector(state => state.rapid7inventory);
    const { assetItems = [], softwareItems = [], selectedDate, selectedGroup } = state;

    const [activeTab, setActiveTab] = useState(0);
    const [group, setGroup] = useState(selectedGroup || "default");
    const [date, setDate] = useState(selectedDate || new Date().toISOString().split("T")[0]);

    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    const handleGroupChange = (event) => {
        const newGroup = event.target.value;
        setGroup(newGroup);
        Controller.get("rapid7inventory").fetchInventory(newGroup, date);
    };

    const handleDateChange = (event) => {
        const newDate = event.target.value;
        setDate(newDate);
        Controller.get("rapid7inventory").fetchInventory(group, newDate);
    };

    const renderTable = (rows) => {
        if (!rows || rows.length === 0) {
            return (
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={6}>No data available</TableCell>
                    </TableRow>
                </TableBody>
            );
        }

        const headers = rows[0];
        const dataRows = rows.slice(1);

        return (
            <>
                <TableHead>
                    <TableRow>
                        {headers.map((header, index) => (
                            <TableCell key={index}><strong>{header}</strong></TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {dataRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {row.map((cell, colIndex) => (
                                <TableCell key={colIndex}>{cell}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </>
        );
    };

    const downloadCSV = () => {
        const data = activeTab === 0 ? assetItems : softwareItems;
        if (!data || data.length === 0) {
            alert("No data available to download.");
            return;
        }

        const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const prefix = group === "default" ? "" : `${group}_`;
        const fileName = activeTab === 0
            ? `${prefix}asset_inventory_${date}.csv`
            : `${prefix}software_inventory_${date}.csv`;

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={classes.root}>
            <Typography variant="h5">Rapid7 Inventory</Typography>

            <FormControl className={classes.groupSelect}>
                <InputLabel id="group-select-label">Group</InputLabel>
                <Select
                    labelId="group-select-label"
                    value={group}
                    onChange={handleGroupChange}
                >
                    <MenuItem value="default">Nuxeo</MenuItem>
                    <MenuItem value="alfresco">Alfresco</MenuItem>
                </Select>
            </FormControl>

            <TextField
                label="Select Date"
                type="date"
                value={date}
                className={classes.dateInput}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
            />

            <div className={classes.tabContainer}>
                <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                    <Tab label={`Asset Inventory (${assetItems.length > 1 ? assetItems.length - 1 : 0})`} />
                    <Tab label={`Software Inventory (${softwareItems.length > 1 ? softwareItems.length - 1 : 0})`} />
                </Tabs>

                <Button
                    variant="contained"
                    color="primary"
                    className={classes.downloadButton}
                    onClick={downloadCSV}
                >
                    Download CSV
                </Button>
            </div>

            <Box mt={2}>
                <TableContainer component={Paper}>
                    <Table className={classes.table} aria-label="rapid7 inventory table">
                        {activeTab === 0 ? renderTable(assetItems) : renderTable(softwareItems)}
                    </Table>
                </TableContainer>
            </Box>
        </div>
    );
};

export default withStyles(styles)(Rapid7Inventory);